import { useState, useEffect, useRef, useMemo, memo } from "react";
import { useNavigate } from "react-router-dom";
import React from "react";
import Lottie from "react-lottie";
import animationData from "../../imagesJson/hiringAnime.json";
import axios, { AxiosError } from "axios";
import { forgetPassword, getCurrentUser, isUserLoggedIn, registerInvestorPortalUser, sendotp, sendPortalRegistrationOtp, userLogin, userLoginWithOtp, verifyotp } from "../../services/apiclient";
import { getUsersGroupedByRole, getMySubordinates } from "../../services/usersapi";
import { toast } from "sonner";
import { useRbac } from "../../context";
import { isAiFunnelOnlyUser } from "../../utils/aiFunnelUser";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog"
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import { BlockedAccountModal } from "../../components/BlockedAccountModal";

/** Isolated from parent state (OTP timers, etc.) so Lottie is not reconciled every second. */
const LoginHeroPanel = memo(function LoginHeroPanel() {
    const options = useMemo(
        () => ({
            loop: true,
            autoplay: true,
            animationData,
            rendererSettings: {
                preserveAspectRatio: "xMidYMid slice" as const,
            },
        }),
        []
    );

    return (
        <div className="bg-login-hero hidden w-full items-center justify-center rounded-xl p-6 sm:p-8 lg:flex lg:w-3/5 lg:min-h-[min(520px,70vh)] lg:p-8 xl:min-h-[560px]">
            <div className="mx-auto flex h-full w-full max-w-[480px] flex-1 items-center justify-center px-2">
                <Lottie options={options} height={360} width={420} style={{ marginTop: "8px", maxWidth: "100%" }} />
            </div>
        </div>
    );
});

const LoginPage: React.FC = () => {
    const [emailFocused, setEmailFocused] = useState(false);
    const [passwordFocused, setPasswordFocused] = useState(false);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [activeTab, setActiveTab] = useState("recruiter");
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string>("");
    const { hasPermission, user, role, setUser } = useRbac();
    
    const navigate = useNavigate();

    // Forgot password states
    const [forgotOpen, setForgotOpen] = useState(false);
    const [step, setStep] = useState(1); // 1: enter email/phone, 2: verify otp, 3: reset password
    const [emailOrPhone, setEmailOrPhone] = useState("");
    const [otp, setOtp] = useState("");
    const [newPass, setNewPass] = useState("");
    const [confirmPass, setConfirmPass] = useState("");
    const [showNewPass, setShowNewPass] = useState(false);
    const [showConfirmPass, setShowConfirmPass] = useState(false);
    const [timer, setTimer] = useState(90);
    const [canResend, setCanResend] = useState(false);
    const [forgotLoading, setForgotLoading] = useState(false);
    const [forgotError, setForgotError] = useState("");
    const [resetToken, setResetToken] = useState("");
    const [showBlockedModal, setShowBlockedModal] = useState(false);

    // OTP login mode
    const [loginMode, setLoginMode] = useState<"password" | "otp">("password");
    const [otpMobile, setOtpMobile] = useState("");
    const [otpCode, setOtpCode] = useState("");
    const [otpSent, setOtpSent] = useState(false);
    const [otpTimer, setOtpTimer] = useState(0);
    const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const registerOtpInputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const [isRegisterMode, setIsRegisterMode] = useState(false);
    const [registerUsername, setRegisterUsername] = useState("");
    const [registerEmail, setRegisterEmail] = useState("");
    const [registerMobile, setRegisterMobile] = useState("");
    const [registerPassword, setRegisterPassword] = useState("");
    const [registerConfirmPassword, setRegisterConfirmPassword] = useState("");
    const [registerLoading, setRegisterLoading] = useState(false);
    const [registerError, setRegisterError] = useState("");
    const [registerOtp, setRegisterOtp] = useState("");
    const [registerOtpLoading, setRegisterOtpLoading] = useState(false);
    const [isRegisterOtpDialogOpen, setIsRegisterOtpDialogOpen] = useState(false);
    const [registerOtpResendTimer, setRegisterOtpResendTimer] = useState(0);
    const [registerOtpResending, setRegisterOtpResending] = useState(false);
    const [registerOtpError, setRegisterOtpError] = useState("");
    const [registerFieldErrors, setRegisterFieldErrors] = useState<{
        username?: string;
        email?: string;
        mobile?: string;
        password?: string;
        confirmPassword?: string;
    }>({});

    useEffect(() => {
        const checkIfUserIsLoggedIn = async () => {
            setIsLoading(true);
            try {
                const res = await getCurrentUser();
                if (!res) {
                    setIsLoading(false);
                    return;
                }

                setIsLoading(false);
                if (res.status === "success") {
                    navigate("/recruiter-onboarding");      }
                else {
                    throw new Error("User is not authenticated");
                }

            } catch (err) {
                setIsLoading(false);
                // Expected when opening the login page without a session — no toast (avoids work + noise).
                console.log("Not logged in or token expired", err);
            }
        };

        checkIfUserIsLoggedIn();
    }, []);

    // Timer for OTP
    useEffect(() => {
        let interval: NodeJS.Timeout | null = null;

        if (step === 2 && timer > 0) {
            interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        } else if (timer === 0) {
            setCanResend(true);
        }

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [step, timer]);

    // OTP login timer
    useEffect(() => {
        let interval: NodeJS.Timeout | null = null;
        if (otpTimer > 0) {
            interval = setInterval(() => setOtpTimer((prev) => prev - 1), 1000);
        }
        return () => { if (interval) clearInterval(interval); };
    }, [otpTimer]);

    // Focus first OTP box when OTP is sent
    useEffect(() => {
        if (otpSent) {
            const t = setTimeout(() => otpInputRefs.current[0]?.focus(), 100);
            return () => clearTimeout(t);
        }
    }, [otpSent]);

    // Focus first register OTP box when dialog opens
    useEffect(() => {
        if (isRegisterOtpDialogOpen) {
            const t = setTimeout(() => registerOtpInputRefs.current[0]?.focus(), 100);
            return () => clearTimeout(t);
        }
    }, [isRegisterOtpDialogOpen]);

    // Investor registration OTP: 30s cooldown before resend
    useEffect(() => {
        if (!isRegisterOtpDialogOpen || registerOtpResendTimer <= 0) return;
        const interval = setInterval(() => {
            setRegisterOtpResendTimer((prev) => (prev <= 1 ? 0 : prev - 1));
        }, 1000);
        return () => clearInterval(interval);
    }, [isRegisterOtpDialogOpen, registerOtpResendTimer]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setErrorMsg("");
        setIsLoading(true);
        console.log("Login response: 555");

        try {
            const response = await userLogin({
                email,
                password,
            });
            if (response.data && response.accessToken) {
                localStorage.setItem("accessToken", response.accessToken);
                setUser(response.data.user);
            }

            // Check if user is blocked FIRST - before any API calls
            if (response.data.user?.isBlocked === true) {
                setIsLoading(false);
                setShowBlockedModal(true);
                return; // Don't navigate or make API calls, show modal instead
            }

            // Only make these API calls if user is NOT blocked
            try {
                const groupedUsersResponse = await getUsersGroupedByRole();
                localStorage.setItem("groupedUsers", JSON.stringify(groupedUsersResponse));
                console.log("Grouped users fetched successfully:", groupedUsersResponse);
                
                const subordinatesResponse = await getMySubordinates();
                localStorage.setItem("mySubordinates", JSON.stringify(subordinatesResponse));
                console.log("My subordinates fetched successfully:", subordinatesResponse);
                
            } catch (apiErr) {
                console.error("Failed to fetch user data:", apiErr);
            }
            if (isAiFunnelOnlyUser(response.data.user)) {
                navigate("/hunar-funnel");
                return;
            }
            if(response.data.user.role === "FBDE" || response.data.user.role === "CONSULTANT"){
                navigate("/create-subjob");
                console.log("user",user)
                return;
            }
            if (response.data.user.role === "INVESTOR") {
                setTimeout(() => {
                    setIsLoading(false);
                    navigate("/investor-dashboard");
                }, 1000);
                return;
            }
            // Add this block for TPO role
            if (response.data.user.role === "TPO") {
                setTimeout(() => {
                    setIsLoading(false);
                    navigate("/TalentPool");
                }, 1000);
                return;
            }
            if (response.data.user.role === "FREELANCE_COUNSELLOR") {
                setTimeout(() => {
                    setIsLoading(false);
                    navigate("/TalentPool");
                }, 1000);
                return;
            }

            setTimeout(() => {
                setIsLoading(false);
                navigate("/recruiter-onboarding");
            }, 1000);

        } catch (err: unknown) {
            let message = "Something went wrong. Please try again.";
            if (axios.isAxiosError(err)) {
                const axiosErr = err as AxiosError<{ message?: string }>;
                if (axiosErr.response && axiosErr.response.data && axiosErr.response.data.message) {
                    message = axiosErr.response.data.message;
                } else if (axiosErr.code === "ECONNREFUSED") {
                    message = "Unable to reach server. Please check your backend.";
                }
            }
            setErrorMsg(message);
            setIsLoading(false);
        }
    };

    const handleSendOtpForLogin = async () => {
        setErrorMsg("");
        const mobile = otpMobile.replace(/\D/g, '').slice(-10);
        if (mobile.length !== 10) {
            setErrorMsg("Please enter a valid 10-digit mobile number");
            return;
        }
        setIsLoading(true);
        try {
            await sendotp(mobile);
            setOtpSent(true);
            setOtpTimer(60);
        } catch {
            setIsLoading(false);
        } finally {
            setIsLoading(false);
        }
    };

    const handleOtpLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrorMsg("");
        setIsLoading(true);
        try {
            const mobile = otpMobile.replace(/\D/g, '').slice(-10);
            const response = await userLoginWithOtp({ emailorphone: mobile, otp: otpCode });
            if (response.data && response.accessToken) {
                localStorage.setItem("accessToken", response.accessToken);
                setUser(response.data.user);
            }
            if (response.data?.user?.isBlocked === true) {
                setIsLoading(false);
                setShowBlockedModal(true);
                return;
            }
            try {
                const groupedUsersResponse = await getUsersGroupedByRole();
                localStorage.setItem("groupedUsers", JSON.stringify(groupedUsersResponse));
                const subordinatesResponse = await getMySubordinates();
                localStorage.setItem("mySubordinates", JSON.stringify(subordinatesResponse));
            } catch { /* ignore */ }
            if (isAiFunnelOnlyUser(response.data?.user)) {
                navigate("/hunar-funnel");
                return;
            }
            const role = response.data?.user?.role;
            if (role === "FBDE" || role === "CONSULTANT") {
                navigate("/create-subjob");
                return;
            }
            if (role === "INVESTOR") {
                navigate("/investor-dashboard");
                return;
            }
            if (role === "TPO" || role === "FREELANCE_COUNSELLOR") {
                navigate("/TalentPool");
                return;
            }
            navigate("/recruiter-onboarding");
        } catch (err: unknown) {
            const message = axios.isAxiosError(err) && err.response?.data?.message
                ? err.response.data.message
                : "Invalid OTP. Please try again.";
            setErrorMsg(message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleTabChange = (tab: string) => {
        setActiveTab(tab);
        setEmail("");
        setPassword("");
        setEmailFocused(false);
        setPasswordFocused(false);
        setErrorMsg("");
    };

    const resetRegisterForm = () => {
        setRegisterUsername("");
        setRegisterEmail("");
        setRegisterMobile("");
        setRegisterPassword("");
        setRegisterConfirmPassword("");
        setRegisterOtp("");
        setIsRegisterOtpDialogOpen(false);
        setRegisterOtpResendTimer(0);
        setRegisterOtpResending(false);
        setRegisterOtpError("");
        setRegisterError("");
        setRegisterFieldErrors({});
    };

    const handleRegisterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setRegisterError("");
        const nextErrors: {
            username?: string;
            email?: string;
            mobile?: string;
            password?: string;
            confirmPassword?: string;
        } = {};

        if (!registerUsername.trim()) {
            nextErrors.username = "Username is required.";
        }
        if (!registerEmail.trim()) {
            nextErrors.email = "Email is required.";
        } else if (!/\S+@\S+\.\S+/.test(registerEmail.trim())) {
            nextErrors.email = "Please enter a valid email address.";
        }
        if (!registerMobile.trim()) {
            nextErrors.mobile = "Mobile number is required.";
        } else if (registerMobile.replace(/\D/g, "").length !== 10) {
            nextErrors.mobile = "Mobile number must be exactly 10 digits.";
        }
        if (!registerPassword) {
            nextErrors.password = "Password is required.";
        } else if (registerPassword.length < 6) {
            nextErrors.password = "Password must be at least 6 characters.";
        }
        if (!registerConfirmPassword) {
            nextErrors.confirmPassword = "Confirm password is required.";
        } else if (registerPassword !== registerConfirmPassword) {
            nextErrors.confirmPassword = "Password and confirm password must match.";
        }
        setRegisterFieldErrors(nextErrors);

        if (Object.keys(nextErrors).length > 0) {
            return;
        }

        const mobile = registerMobile.replace(/\D/g, "").slice(0, 10);

        setRegisterLoading(true);
        try {
            setRegisterOtp("");
            setRegisterOtpError("");
            await sendPortalRegistrationOtp(mobile);
            setIsRegisterOtpDialogOpen(true);
            setRegisterOtpResendTimer(30);
            toast.success("OTP sent to your mobile number.");
        } catch {
            // sendPortalRegistrationOtp already surfaces toast on failure
        } finally {
            setRegisterLoading(false);
        }
    };

    const handleFinalRegisterSubmit = async () => {
        setRegisterOtpError("");
        if (!registerOtp.trim()) {
            setRegisterOtpError("OTP is required.");
            return;
        }
        if (!/^\d{6}$/.test(registerOtp.trim())) {
            setRegisterOtpError("OTP must be a 6-digit number.");
            return;
        }

        setRegisterOtpLoading(true);
        try {
            const mobile = registerMobile.replace(/\D/g, "").slice(0, 10);
            const res = await registerInvestorPortalUser({
                username: registerUsername.trim(),
                email: registerEmail.trim(),
                phone: mobile,
                password: registerPassword,
                location: "",
                otp: registerOtp.trim(),
            });
            if (res?.data?.user) {
                setUser(res.data.user);
            }
            if (res?.data?.user?.isBlocked === true) {
                setShowBlockedModal(true);
                resetRegisterForm();
                setIsRegisterMode(false);
                return;
            }
            try {
                const groupedUsersResponse = await getUsersGroupedByRole();
                localStorage.setItem("groupedUsers", JSON.stringify(groupedUsersResponse));
                const subordinatesResponse = await getMySubordinates();
                localStorage.setItem("mySubordinates", JSON.stringify(subordinatesResponse));
            } catch {
                /* non-blocking */
            }
            resetRegisterForm();
            setIsRegisterMode(false);
            navigate("/investor-dashboard");
        } catch (err: unknown) {
            const message = axios.isAxiosError(err) && err.response?.data?.message
                ? err.response.data.message
                : "Registration failed. Please try again.";
            setRegisterOtpError(message);
        } finally {
            setRegisterOtpLoading(false);
        }
    };

    const handleRegisterResendOtp = async () => {
        if (registerOtpResendTimer > 0 || registerOtpResending) return;
        const mobile = registerMobile.replace(/\D/g, "").slice(0, 10);
        if (mobile.length !== 10) {
            setRegisterOtpError("Invalid mobile for resend.");
            return;
        }
        setRegisterOtpResending(true);
        setRegisterOtpError("");
        try {
            await sendPortalRegistrationOtp(mobile);
            setRegisterOtp("");
            setRegisterOtpResendTimer(30);
            toast.success("OTP sent again.");
        } catch {
            // sendPortalRegistrationOtp already surfaces toast on failure
        } finally {
            setRegisterOtpResending(false);
        }
    };

    const getTabSubtitle = () => {
        switch (activeTab) {
            case "recruiter": return "Access your recruiter dashboard";
            case "college": return "Connect to your college portal";
            case "agency": return "Manage your agency account";
            default: return "Access your recruiter dashboard";
        }
    };

    const getTabIcon = (tab: string) => {
        switch (tab) {
            case "recruiter":
                return (
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="9" />
                        <circle cx="12" cy="8" r="2.4" />
                        <path d="M8 20v-1a4 4 0 018 0v1H8z" />
                        <path d="M18.5 18.5l4.5 4.5" />
                    </svg>
                );
            case "college":
                return (
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l9-5-9-5-9 5 9 5z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                    </svg>
                );
            case "agency":
                return (
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                );
        }
    };

    // Forgot password handlers
    const handleSendOtp = async () => {
        setForgotError("");
        setForgotLoading(true);
        try {
            await sendotp(emailOrPhone);
            setStep(2);
            setTimer(30);
            setCanResend(false);
            toast.success("OTP sent successfully");
        } catch (err) {
            let message = "Failed to send OTP. Please try again.";
            if (axios.isAxiosError(err)) {
                const axiosErr = err as AxiosError<{ message?: string }>;
                if (axiosErr.response?.data?.message) {
                    message = axiosErr.response.data.message;
                }
            }
            setForgotError(message);
        } finally {
            setForgotLoading(false);
        }
    };

    const handleVerifyOtp = async () => {
        setForgotError("");
        setForgotLoading(true);
        try {
            const response = await verifyotp(emailOrPhone, otp);
            setStep(3);
            toast.success("OTP verified");
        } catch (err) {
            let message = "Invalid OTP. Please try again.";
            if (axios.isAxiosError(err)) {
                const axiosErr = err as AxiosError<{ message?: string }>;
                if (axiosErr.response?.data?.message) {
                    message = axiosErr.response.data.message;
                }
            }
            setForgotError(message);
        } finally {
            setForgotLoading(false);
        }
    };

    const handleResetPassword = async () => {
        setForgotError("");
        if (newPass !== confirmPass) {
            setForgotError("Passwords do not match");
            return;
        }
        setForgotLoading(true);
        try {
            await forgetPassword(newPass, confirmPass, emailOrPhone);
            toast.success("Password reset successfully");
            setForgotOpen(false);
            setStep(1);
            setEmailOrPhone("");
            setOtp("");
            setNewPass("");
            setConfirmPass("");
            setResetToken("");
        } catch (err) {
            let message = "Failed to reset password. Please try again.";
            if (axios.isAxiosError(err)) {
                const axiosErr = err as AxiosError<{ message?: string }>;
                if (axiosErr.response?.data?.message) {
                    message = axiosErr.response.data.message;
                }
            }
            setForgotError(message);
        } finally {
            setForgotLoading(false);
        }
    };

    const handleResendOtp = async () => {
        setCanResend(false);
        setTimer(30);
        await handleSendOtp();
    };

    // Mask email or phone for OTP sent message
    const maskInput = (input: string) => {
        if (input.includes("@")) {
            const [localPart, domain] = input.split("@");
            return `******${localPart.slice(-4)}@${domain}`;
        } else {
            return `******${input.slice(-4)}`;
        }
    };

    return (
        <>
        <div className="min-h-screen relative overflow-hidden bg-black">
            <div className="absolute inset-0 bg-white"></div>

            <div className="relative z-10 flex justify-center items-center min-h-screen p-3 sm:p-4 lg:p-6">
                <div className="flex w-full max-w-5xl flex-col items-center justify-center rounded-2xl bg-white shadow-xl sm:rounded-3xl lg:flex-row lg:items-stretch">
                    {/* Left Side - Hero Section (memo: avoids re-render on OTP timer ticks) */}
                    <LoginHeroPanel />
                

                {/* Right Side - Login Form */}
                <div className="flex w-full items-center justify-center p-5 sm:p-6 lg:w-2/5 lg:flex-none lg:p-8 xl:p-10">
                    
                    <div className="w-full max-w-sm flex flex-col justify-center">
                        {!isRegisterMode ? (
                            <div className="flex justify-center items-center mb-5 sm:mb-6">
                               <img
                                            src="/logo512.png"
                                            alt="EarlyJobs"
                                            className="w-20 h-12 sm:w-24 sm:h-14 object-cover rounded-xl"
                                        />
                            </div>
                        ) : null}
                               
                        {/* Tab Selection */}
                        {/* <div className="flex justify-center mb-8">
                            <div
                                className="inline-flex gap-2 bg-white/20 backdrop-blur-sm rounded-2xl p-2 shadow-lg border border-white/20"
                                style={{ flexWrap: "wrap", justifyContent: "center" }}
                            >
                                {["recruiter", "college", "agency"].map((tab) => (
                                    <button
                                        key={tab}
                                        onClick={() => handleTabChange(tab)}
                                        className={[
                                            "flex items-center px-5 py-2.5 rounded-xl font-medium text-sm transition-all duration-300 font-semibold",
                                            activeTab === tab
                                                ? "bg-[#E7684B] text-white shadow-lg transform scale-105"
                                                : "text-[#000] hover:text-[#E7684B] hover:bg-[#FFE5C8]/50 hover:shadow-md",
                                        ].join(" ")}
                                    >
                                        {getTabIcon(tab)}
                                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                                    </button>
                                ))}
                            </div>
                        </div> */}

                        <div>
                            <div className="flex flex-col items-center mb-5 sm:mb-6 space-y-1.5">
                                <div className="flex items-center gap-3">
                                    {isRegisterMode ? (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                resetRegisterForm();
                                                setIsRegisterMode(false);
                                            }}
                                            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#E7684B]/30 text-[#E7684B] transition-all duration-200 hover:bg-orange-50"
                                            aria-label="Back to login"
                                        >
                                            <ArrowLeft className="h-4 w-4" />
                                        </button>
                                    ) : null}
                                    <h2 className="text-xl sm:text-2xl font-bold text-[#E6684B]">
                                        {isRegisterMode ? "Investor Registration" : "Welcome Back"}
                                    </h2>
                                </div>
                                <p className="text-gray-700 text-xs sm:text-sm text-center">
                                    {isRegisterMode ? "Create your investor account" : getTabSubtitle()}
                                </p>
                            </div>

                            {isRegisterMode ? (
                                <form onSubmit={handleRegisterSubmit} className="space-y-4 sm:space-y-6">
                                    <div className="space-y-1">
                                        <input
                                            type="text"
                                            value={registerUsername}
                                            onChange={(e) => {
                                                setRegisterUsername(e.target.value);
                                                setRegisterFieldErrors((prev) => ({ ...prev, username: undefined }));
                                            }}
                                            className={`w-full px-3 py-3 sm:px-4 sm:py-4 bg-gray-50/50 border-2 rounded-xl sm:rounded-2xl focus:outline-none transition-all duration-300 text-sm sm:text-base text-gray-900 ${registerFieldErrors.username ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-[#EA580C]"}`}
                                            placeholder="Username"
                                            required
                                        />
                                        {registerFieldErrors.username ? <p className="text-xs text-red-600">{registerFieldErrors.username}</p> : null}
                                    </div>
                                    <div className="space-y-1">
                                        <input
                                            type="email"
                                            value={registerEmail}
                                            onChange={(e) => {
                                                setRegisterEmail(e.target.value);
                                                setRegisterFieldErrors((prev) => ({ ...prev, email: undefined }));
                                            }}
                                            className={`w-full px-3 py-3 sm:px-4 sm:py-4 bg-gray-50/50 border-2 rounded-xl sm:rounded-2xl focus:outline-none transition-all duration-300 text-sm sm:text-base text-gray-900 ${registerFieldErrors.email ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-[#EA580C]"}`}
                                            placeholder="Email"
                                            required
                                        />
                                        {registerFieldErrors.email ? <p className="text-xs text-red-600">{registerFieldErrors.email}</p> : null}
                                    </div>
                                    <div className="space-y-1">
                                        <input
                                            type="tel"
                                            value={registerMobile}
                                            onChange={(e) => {
                                                setRegisterMobile(e.target.value.replace(/\D/g, "").slice(0, 10));
                                                setRegisterOtp("");
                                                setIsRegisterOtpDialogOpen(false);
                                                setRegisterOtpError("");
                                                setRegisterFieldErrors((prev) => ({ ...prev, mobile: undefined }));
                                            }}
                                            className={`w-full px-3 py-3 sm:px-4 sm:py-4 bg-gray-50/50 border-2 rounded-xl sm:rounded-2xl focus:outline-none transition-all duration-300 text-sm sm:text-base text-gray-900 ${registerFieldErrors.mobile ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-[#EA580C]"}`}
                                            placeholder="Mobile"
                                            maxLength={10}
                                            required
                                        />
                                        {registerFieldErrors.mobile ? <p className="text-xs text-red-600">{registerFieldErrors.mobile}</p> : null}
                                    </div>
                                    <div className="space-y-1">
                                        <input
                                            type="password"
                                            value={registerPassword}
                                            onChange={(e) => {
                                                setRegisterPassword(e.target.value);
                                                setRegisterFieldErrors((prev) => ({ ...prev, password: undefined, confirmPassword: undefined }));
                                            }}
                                            className={`w-full px-3 py-3 sm:px-4 sm:py-4 bg-gray-50/50 border-2 rounded-xl sm:rounded-2xl focus:outline-none transition-all duration-300 text-sm sm:text-base text-gray-900 ${registerFieldErrors.password ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-[#EA580C]"}`}
                                            placeholder="Password"
                                            required
                                        />
                                        {registerFieldErrors.password ? <p className="text-xs text-red-600">{registerFieldErrors.password}</p> : null}
                                    </div>
                                    <div className="space-y-1">
                                        <input
                                            type="password"
                                            value={registerConfirmPassword}
                                            onChange={(e) => {
                                                setRegisterConfirmPassword(e.target.value);
                                                setRegisterFieldErrors((prev) => ({ ...prev, confirmPassword: undefined }));
                                            }}
                                            className={`w-full px-3 py-3 sm:px-4 sm:py-4 bg-gray-50/50 border-2 rounded-xl sm:rounded-2xl focus:outline-none transition-all duration-300 text-sm sm:text-base text-gray-900 ${registerFieldErrors.confirmPassword ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-[#EA580C]"}`}
                                            placeholder="Confirm password"
                                            required
                                        />
                                        {registerFieldErrors.confirmPassword ? <p className="text-xs text-red-600">{registerFieldErrors.confirmPassword}</p> : null}
                                    </div>
                                    {registerError && (
                                        <div className="text-red-600 text-xs sm:text-sm text-center">
                                            {registerError}
                                        </div>
                                    )}
                                    <button
                                        type="submit"
                                        disabled={registerLoading}
                                        className="w-full py-3 sm:py-4 bg-[#E7684B] text-white font-semibold rounded-xl sm:rounded-2xl transition-all duration-300 ease-in-out text-sm sm:text-base hover:shadow-lg hover:-translate-y-[1px] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {registerLoading ? "Sending OTP..." : "Send OTP"}
                                    </button>
                                </form>
                            ) : (
                            <form
                                onSubmit={loginMode === "otp" ? handleOtpLogin : handleSubmit}
                                className="space-y-4 sm:space-y-6"
                            >
                                {loginMode === "password" ? (
                                    <>
                                        <div className="relative">
                                            <input
                                                type="email"
                                                id="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                onFocus={() => setEmailFocused(true)}
                                                onBlur={() => setEmailFocused(false)}
                                                className="w-full px-3 py-3 sm:px-4 sm:py-4 bg-gray-50/50 border-2 border-gray-200 rounded-xl sm:rounded-2xl focus:outline-none focus:border-[#EA580C] transition-all duration-300 placeholder-transparent peer text-sm sm:text-base text-gray-900"
                                                placeholder=" "
                                                required
                                            />
                                            <label
                                                htmlFor="email"
                                                style={{ borderRadius: "0.5rem", boxShadow: emailFocused || email ? "0 1px 3px rgba(0, 0, 0, 0.1)" : "none" }}
                                                className={[
                                                    "absolute left-3 sm:left-4 transition-all duration-300 pointer-events-none",
                                                    emailFocused || email
                                                        ? "-top-2 text-xs bg-white px-2 text-orange-600 font-medium"
                                                        : "top-3 sm:top-4 text-sm sm:text-base text-gray-500",
                                                ].join(" ")}
                                            >
                                                Email Address
                                            </label>
                                            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-orange-400/20 via-purple-500/20 to-pink-500/20 opacity-0 peer-focus:opacity-100 transition-opacity duration-300 -z-10"></div>
                                        </div>

                                        <div className="relative">
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                id="password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                onFocus={() => setPasswordFocused(true)}
                                                onBlur={() => setPasswordFocused(false)}
                                                className="w-full px-3 py-3 sm:px-4 sm:py-4 bg-gray-50/50 border-2 border-gray-200 rounded-xl sm:rounded-2xl focus:outline-none focus:border-[#EA580C] transition-all duration-300 placeholder-transparent peer text-sm sm:text-base text-gray-900"
                                                placeholder=" "
                                                required
                                            />
                                            <label
                                                htmlFor="password"
                                                style={{ borderRadius: "0.5rem", boxShadow: passwordFocused || password ? "0 1px 3px rgba(0, 0, 0, 0.1)" : "none" }}
                                                className={[
                                                    "absolute left-3 sm:left-4 transition-all duration-300 pointer-events-none",
                                                    passwordFocused || password
                                                        ? "-top-2 text-xs bg-white px-2 text-purple-600 font-medium"
                                                        : "top-3 sm:top-4 text-sm sm:text-base text-gray-500",
                                                ].join(" ")}
                                            >
                                                Password
                                            </label>
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-3 sm:right-4 top-3 sm:top-4 text-gray-500 hover:text-gray-700 transition-colors duration-200"
                                                aria-label={showPassword ? "Hide password" : "Show password"}
                                            >
                                                {showPassword ? (
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L8.05 8.05m1.828 1.828l4.242 4.242M8.05 8.05l2.122 2.122m5.656 5.656L17.95 17.95M8.05 8.05L3.05 3.05m14.142 14.142l5 5" />
                                                    </svg>
                                                ) : (
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                    </svg>
                                                )}
                                            </button>
                                            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-purple-400/20 via-pink-500/20 to-orange-500/20 opacity-0 peer-focus:opacity-100 transition-opacity duration-300 -z-10"></div>
                                        </div>

                                        <div className="flex items-center justify-between text-sm">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setLoginMode("otp");
                                                    setErrorMsg("");
                                                    setOtpMobile("");
                                                    setOtpCode("");
                                                    setOtpSent(false);
                                                }}
                                                className="text-[#E7684B] hover:text-orange-700 font-medium hover:underline transition-colors duration-200"
                                            >
                                                Sign in with OTP
                                            </button>
                                            <a
                                                href="#"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setForgotOpen(true);
                                                }}
                                                className="text-[#E7684B] hover:text-orange-700 font-medium hover:underline transition-colors duration-200"
                                            >
                                                Forgot password?
                                            </a>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="relative">
                                            <input
                                                type="tel"
                                                id="otpMobile"
                                                value={otpMobile}
                                                onChange={(e) => setOtpMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                                                className="w-full px-3 py-3 sm:px-4 sm:py-4 bg-gray-50/50 border-2 border-gray-200 rounded-xl sm:rounded-2xl focus:outline-none focus:border-[#EA580C] transition-all duration-300 text-sm sm:text-base text-gray-900"
                                                placeholder="Mobile number (10 digits)"
                                                maxLength={10}
                                                disabled={otpSent}
                                            />
                                        </div>

                                        {otpSent && (
                                            <div className="relative">
                                                <div className="flex gap-2 sm:gap-3 w-full mb-3">
                                                    {[0, 1, 2, 3, 4, 5].map((i) => (
                                                        <input
                                                            key={i}
                                                            ref={(el) => { otpInputRefs.current[i] = el; }}
                                                            type="text"
                                                            inputMode="numeric"
                                                            maxLength={1}
                                                            value={otpCode[i] ?? ""}
                                                            onChange={(e) => {
                                                                const v = e.target.value.replace(/\D/g, "");
                                                                if (v) {
                                                                    const next = otpCode.slice(0, i) + v.slice(-1) + otpCode.slice(i + 1);
                                                                    setOtpCode(next.slice(0, 6));
                                                                    if (i < 5) otpInputRefs.current[i + 1]?.focus();
                                                                }
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Backspace" && !otpCode[i] && i > 0) {
                                                                    setOtpCode(otpCode.slice(0, i - 1) + otpCode.slice(i));
                                                                    otpInputRefs.current[i - 1]?.focus();
                                                                } else if (e.key === "Backspace" && otpCode[i]) {
                                                                    setOtpCode(otpCode.slice(0, i) + otpCode.slice(i + 1));
                                                                }
                                                            }}
                                                            onPaste={(e) => {
                                                                e.preventDefault();
                                                                const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
                                                                setOtpCode(pasted);
                                                                const nextIdx = Math.min(pasted.length, 5);
                                                                otpInputRefs.current[nextIdx]?.focus();
                                                            }}
                                                            className="flex-1 min-w-0 h-12 sm:h-14 text-center text-lg sm:text-xl font-semibold bg-gray-50/50 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#EA580C] transition-all duration-300 text-gray-900"
                                                        />
                                                    ))}
                                                </div>
                                                <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                                                    {otpTimer > 0 ? (
                                                        <span>Resend OTP in {otpTimer}s</span>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={handleSendOtpForLogin}
                                                            className="text-[#E7684B] hover:underline font-medium"
                                                        >
                                                            Resend OTP
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {!otpSent && (
                                            <button
                                                type="button"
                                                onClick={handleSendOtpForLogin}
                                                disabled={isLoading || otpMobile.replace(/\D/g, "").length !== 10}
                                                className="w-full py-3 sm:py-4 bg-[#E7684B] text-white font-semibold rounded-xl sm:rounded-2xl transition-all duration-300 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                                            >
                                                {isLoading ? "Sending OTP..." : "Send OTP"}
                                            </button>
                                        )}

                                        <button
                                            type="button"
                                            onClick={() => {
                                                setLoginMode("password");
                                                setErrorMsg("");
                                                setOtpMobile("");
                                                setOtpCode("");
                                                setOtpSent(false);
                                            }}
                                            className="text-[#E7684B] hover:text-orange-700 font-medium hover:underline transition-colors duration-200 text-sm w-full text-left"
                                        >
                                            ← Back to password
                                        </button>
                                    </>
                                )}

                                {errorMsg && (
                                    <div className="text-red-600 text-xs sm:text-sm mb-2 text-center">
                                        {errorMsg}
                                    </div>
                                )}

                                {(loginMode === "password" || otpSent) && (
                                    <>
                                        <button
                                            type="submit"
                                            disabled={isLoading || (loginMode === "otp" && (!otpCode || otpCode.length < 6))}
                                            className={[
                                                "w-full py-3 sm:py-4 bg-[#E7684B] text-white font-semibold rounded-xl sm:rounded-2xl transition-all duration-300 ease-in-out text-sm sm:text-base",
                                                isLoading ? "opacity-50 cursor-not-allowed" : "hover:shadow-lg hover:-translate-y-[1px] cursor-pointer",
                                            ].join(" ")}
                                        >
                                            {isLoading ? (
                                                <div className="flex items-center justify-center">
                                                    <svg className="animate-spin h-5 w-5 mr-3 text-white" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                                                    </svg>
                                                    Signing you in...
                                                </div>
                                            ) : (
                                                "Sign In"
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setErrorMsg("");
                                                setIsRegisterMode(true);
                                            }}
                                            className="w-full text-center text-sm sm:text-base font-medium text-[#E7684B] hover:text-orange-700 hover:underline transition-colors duration-200"
                                        >
                                            Investor Registration
                                        </button>
                                    </>
                                )}
                            </form>
                            )}
                        </div>
                    </div>
                </div>

                </div>
            </div>

            {/* Forgot Password Dialog */}

{forgotOpen && (
  <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
    <DialogContent className="max-w-md w-[90%] rounded-2xl border border-gray-200 bg-white p-4 sm:w-[95%] sm:rounded-3xl sm:p-6">
      <DialogHeader>
        <DialogTitle className="text-xl sm:text-2xl font-bold text-[#E6684B] text-center">
          {step === 1 && "Forgot Password"}
          {step === 2 && "Verify OTP"}
          {step === 3 && "Reset Password"}
        </DialogTitle>
      </DialogHeader>

      {/* ----------------- STEP 1 ----------------- */}
      {step === 1 && (
        <div className="space-y-4 sm:space-y-6">
          <p className="text-center text-gray-600 text-sm sm:text-base">
            Enter your email or phone number to receive an OTP
          </p>
          <input
            type="text"
            value={emailOrPhone}
            onChange={(e) => setEmailOrPhone(e.target.value)}
            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-100/50 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#EA580C] transition-all placeholder-gray-500 text-sm sm:text-base text-gray-900"
            placeholder="Email or phone number"
          />
          {forgotError && (
            <p className="text-red-600 text-xs sm:text-sm text-center">{forgotError}</p>
          )}
          <button
            onClick={handleSendOtp}
            disabled={forgotLoading}
            className={[
              "w-full py-2.5 sm:py-3 bg-[#E7684B] text-white font-semibold rounded-xl transition-all text-sm sm:text-base",
              forgotLoading
                ? "opacity-50 cursor-not-allowed"
                : "hover:shadow-xl hover:-translate-y-[1px]",
            ].join(" ")}
          >
            {forgotLoading ? "Sending..." : "Send OTP"}
          </button>
        </div>
      )}

      {/* ----------------- STEP 2 ----------------- */}
      {step === 2 && (
        <div className="space-y-4 sm:space-y-6">
          <p className="text-center text-gray-600 text-sm sm:text-base">
            OTP sent to {maskInput(emailOrPhone)}
          </p>
          <input
            type="text"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-100/50 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#EA580C] transition-all placeholder-gray-500 text-sm sm:text-base text-gray-900"
            placeholder="Enter OTP"
          />
          <p className="text-center text-gray-600 text-sm sm:text-base">
            Time remaining: <span className="font-medium">{timer}s</span>
          </p>
          {forgotError && (
            <p className="text-red-600 text-xs sm:text-sm text-center">{forgotError}</p>
          )}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <button
            onClick={handleVerifyOtp}
            disabled={forgotLoading}
            className={[
              "w-full py-2.5 sm:py-3 bg-[#E7684B] text-white font-semibold rounded-xl transition-all text-sm sm:text-base",
              forgotLoading
                ? "opacity-50 cursor-not-allowed"
                : "hover:shadow-xl hover:-translate-y-[1px]",
            ].join(" ")}
          >
            {forgotLoading ? "Verifying..." : "Verify OTP"}
          </button>
          <button
            onClick={handleResendOtp}
            disabled={!canResend || forgotLoading}
            className={[
              "w-full py-2.5 sm:py-3 bg-transparent text-[#E7684B] font-semibold rounded-xl border-2 border-[#E7684B] transition-all text-sm sm:text-base",
              !canResend || forgotLoading
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-[#E7684B] hover:text-white hover:shadow-xl",
            ].join(" ")}
          >
            Resend OTP
          </button>
          </div>
        </div>
      )}

      {/* ----------------- STEP 3 ----------------- */}
      {step === 3 && (
        <div className="space-y-4 sm:space-y-6">
          <p className="text-center text-gray-600 text-sm sm:text-base">Enter your new password</p>

          {/* New Password */}
          <div className="relative">
            <input
              type={showNewPass ? "text" : "password"}
              value={newPass}
              onChange={(e) => setNewPass(e.target.value)}
              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-100/50 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#EA580C] transition-all placeholder-gray-500 text-sm sm:text-base text-gray-900"
              placeholder="New password"
            />
            <button
              type="button"
              onClick={() => setShowNewPass(!showNewPass)}
              className="absolute right-3 sm:right-4 top-3 sm:top-4 text-gray-500 hover:text-gray-700"
            >
              {!showNewPass ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4"/>}
            </button>
          </div>

          {/* Confirm Password */}
          <div className="relative">
            <input
              type={showConfirmPass ? "text" : "password"}
              value={confirmPass}
              onChange={(e) => setConfirmPass(e.target.value)}
              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-100/50 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#EA580C] transition-all placeholder-gray-500 text-sm sm:text-base text-gray-900"
              placeholder="Confirm password"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPass(!showConfirmPass)}
              className="absolute right-3 sm:right-4 top-3 sm:top-4 text-gray-500 hover:text-gray-700"
            >
              {!showConfirmPass ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4"/>}
            </button>
          </div>

          {forgotError && (
            <p className="text-red-600 text-xs sm:text-sm text-center">{forgotError}</p>
          )}
          <button
            onClick={handleResetPassword}
            disabled={forgotLoading}
            className={[
              "w-full py-2.5 sm:py-3 bg-[#E7684B] text-white font-semibold rounded-xl transition-all text-sm sm:text-base",
              forgotLoading
                ? "opacity-50 cursor-not-allowed"
                : "hover:shadow-xl hover:-translate-y-[1px]",
            ].join(" ")}
          >
            {forgotLoading ? "Resetting..." : "Reset Password"}
          </button>
        </div>
      )}

      {/* <DialogFooter>
        <button
          onClick={() => {
            setForgotOpen(false);
            setStep(1);
            setForgotError("");
            setEmailOrPhone("");
            setOtp("");
            setNewPass("");
            setConfirmPass("");
          }}
          className="text-gray-600 hover:text-gray-800 text-sm underline"
        >
          Cancel
        </button>
      </DialogFooter> */}
    </DialogContent>
  </Dialog>
)}

{isRegisterOtpDialogOpen && (
  <Dialog
    open={isRegisterOtpDialogOpen}
    onOpenChange={(open) => {
        setIsRegisterOtpDialogOpen(open);
        if (!open) {
            setRegisterOtpResendTimer(0);
            setRegisterOtpResending(false);
        }
    }}
  >
    <DialogContent className="max-w-md w-[90%] rounded-2xl border border-gray-200 bg-white p-4 sm:w-[95%] sm:rounded-3xl sm:p-6">
      <DialogHeader>
        <DialogTitle className="text-xl sm:text-2xl font-bold text-[#E6684B] text-center">
          Verify OTP
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-4 sm:space-y-6">
        <p className="text-center text-gray-600 text-sm sm:text-base">
          Enter the 6-digit OTP sent to {registerMobile}. It expires in 2 minutes.
        </p>
        <div className="grid grid-cols-6 gap-2 w-full">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <input
              key={i}
              ref={(el) => { registerOtpInputRefs.current[i] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={registerOtp[i] ?? ""}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, "");
                if (!v) return;
                const current = registerOtp.split("");
                current[i] = v.slice(-1);
                const next = current.join("").slice(0, 6);
                setRegisterOtp(next);
                setRegisterOtpError("");
                if (i < 5) registerOtpInputRefs.current[i + 1]?.focus();
              }}
              onKeyDown={(e) => {
                if (e.key === "Backspace") {
                  e.preventDefault();
                  const current = registerOtp.split("");
                  if (current[i]) {
                    current[i] = "";
                    setRegisterOtp(current.join(""));
                    setRegisterOtpError("");
                  } else if (i > 0) {
                    current[i - 1] = "";
                    setRegisterOtp(current.join(""));
                    registerOtpInputRefs.current[i - 1]?.focus();
                    setRegisterOtpError("");
                  }
                } else if (e.key === "ArrowLeft" && i > 0) {
                  registerOtpInputRefs.current[i - 1]?.focus();
                } else if (e.key === "ArrowRight" && i < 5) {
                  registerOtpInputRefs.current[i + 1]?.focus();
                }
              }}
              onPaste={(e) => {
                e.preventDefault();
                const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
                if (!pasted) return;
                setRegisterOtp(pasted);
                setRegisterOtpError("");
                const nextIdx = Math.min(pasted.length, 5);
                registerOtpInputRefs.current[nextIdx]?.focus();
              }}
              className={`w-full min-w-0 h-12 sm:h-14 text-center text-base sm:text-lg font-semibold bg-gray-100/50 border-2 rounded-xl focus:outline-none transition-all text-gray-900 ${registerOtpError ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-[#EA580C]"}`}
            />
          ))}
        </div>
        {registerOtpError ? (
          <p className="text-red-600 text-xs sm:text-sm text-center">{registerOtpError}</p>
        ) : null}
        <div className="flex flex-col items-center gap-2">
          <button
            type="button"
            onClick={handleRegisterResendOtp}
            disabled={registerOtpResendTimer > 0 || registerOtpResending || registerOtpLoading}
            className="text-sm font-semibold text-[#E7684B] underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
          >
            {registerOtpResending
              ? "Sending…"
              : registerOtpResendTimer > 0
                ? `Resend OTP in ${registerOtpResendTimer}s`
                : "Resend OTP"}
          </button>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={() => {
              setIsRegisterOtpDialogOpen(false);
              setRegisterOtp("");
              setRegisterOtpError("");
              setRegisterOtpResendTimer(0);
              setRegisterOtpResending(false);
            }}
            className="w-full py-2.5 sm:py-3 bg-transparent text-[#E7684B] font-semibold rounded-xl border-2 border-[#E7684B] transition-all text-sm sm:text-base hover:bg-[#E7684B] hover:text-white hover:shadow-xl"
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleFinalRegisterSubmit}
            disabled={registerOtpLoading}
            className="w-full py-2.5 sm:py-3 bg-[#E7684B] text-white font-semibold rounded-xl transition-all text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-xl hover:-translate-y-[1px]"
          >
            {registerOtpLoading ? "Verifying..." : "Verify OTP & Register"}
          </button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
)}

            <style>{`
/* Static layered gradient — same palette as before, no infinite repaint animation */
.bg-login-hero {
  background:
    radial-gradient(circle at 20% 30%, rgba(255, 127, 80, 0.42), transparent 42%),
    radial-gradient(circle at 72% 68%, rgba(231, 104, 75, 0.38), transparent 48%),
    radial-gradient(circle at 50% 50%, rgba(255, 179, 71, 0.32), transparent 52%),
    radial-gradient(circle at 40% 18%, rgba(255, 255, 255, 0.55), transparent 35%),
    linear-gradient(145deg, #fff8f4 0%, #ffeadd 42%, #ffd0bc 100%);
}
`}</style>
        </div>
        <BlockedAccountModal
          isOpen={showBlockedModal}
          userEmail={user?.email}
          userName={user?.username}
        />
        </>
    );
};

export default LoginPage;